import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, MotionConfig, LayoutGroup } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { ChatVoice } from "@/components/ChatVoice";
import { JarvisOrb } from "@/components/JarvisOrb";
import { WidgetDeck } from "@/components/WidgetDeck";
import { widgetEmit } from "@/lib/widgetBus";
import { mapControl } from "@/lib/mapControlBus";
import { VoiceSession } from "@/components/VoiceSession";
import { LiveSession } from "@/components/LiveSession";
import { WakeWord } from "@/components/WakeWord";
import { RecordSpotlight } from "@/components/RecordSpotlight";
import { ReviewConfirm } from "@/components/panels/ReviewConfirm";
import { toast } from "sonner";
import { SceneBackdrop } from "@/components/SceneBackdrop";
import { sceneCue } from "@/lib/sceneBus";
import { TodayPanel } from "@/components/panels/TodayPanel";
import { TasksPanel, ClientsPanel, ProjectsPanel } from "@/components/panels/RecordsPanels";
import { ProposalsPanel } from "@/components/panels/ProposalsPanel";
import { EmailsPanel } from "@/components/panels/EmailsPanel";
import { FilesPanel } from "@/components/panels/FilesPanel";
import { MemoryPanel, UsagePanel, NotificationsPanel, CalendarPanel, SettingsPanel } from "@/components/panels/MiscPanels";
import { FinancePanel } from "@/components/panels/FinancePanel";
import {
  LayoutDashboard, CheckSquare, Hammer, Users, FileText, Mail, FolderOpen,
  Calendar, Bell, Database, DollarSign, Settings, HardHat, LogOut, MessageSquare, X, Menu, ArrowLeft, Star, Mic, Landmark,
} from "lucide-react";

const NAV_GROUPS = [
  ["İşler", [["today", LayoutDashboard], ["tasks", CheckSquare], ["projects", Hammer], ["clients", Users], ["site", HardHat]]],
  ["İletişim", [["emails", Mail], ["calendar", Calendar], ["notifications", Bell]]],
  ["Belgeler", [["proposals", FileText], ["finance", Landmark], ["files", FolderOpen]]],
  ["Sistem", [["memory", Database], ["usage", DollarSign], ["settings", Settings]]],
];
const NAV = NAV_GROUPS.flatMap(([, items]) => items);
const EASE = [0.22, 1, 0.36, 1];
const SPOT_KINDS = new Set(["proposals", "projects", "files"]);

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { t, tx, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [orbState, setOrbState] = useState("idle");
  const [aiStatus, setAiStatus] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [talkOpen, setTalkOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [focus, setFocus] = useState(null);
  const [bigWidget, setBigWidget] = useState(false);
  const [mapScene, setMapScene] = useState(false);   // contextual map behind the orb
  const [favs, setFavs] = useState(() => { try { return JSON.parse(localStorage.getItem("jarvis-favs") || "[]"); } catch { return []; } });
  const toggleFav = (key) => setFavs((cur) => {
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    localStorage.setItem("jarvis-favs", JSON.stringify(next));
    return next;
  });
  const ICON_BY_KEY = Object.fromEntries(NAV.map(([k, I]) => [k, I]));
  const [spotlight, setSpotlight] = useState(null); // {kind,id,label,screen} — opened only by a real open_record action
  const [pendingConfirm, setPendingConfirm] = useState(null); // staged gmail_modify/automation write awaiting owner's on-screen approval
  // voice choice: "live" (own-OpenAI GPT-Live) | "standard" | "off"; null = not loaded. Never touches ai_provider.
  const [voiceMode, setVoiceMode] = useState(null);
  const liveMode = voiceMode === null ? null : voiceMode === "live";
  const [wakeEnabled, setWakeEnabled] = useState(false); // "Jarvis diyerek uyandır" — per-user pref, default OFF
  const [voiceConn, setVoiceConn] = useState({ status: "off" }); // reported by whichever engine is mounted
  const voiceRef = useRef(null);                                  // shared imperative control for the orb
  const [sweep, setSweep] = useState(false); // HUD transition: on when an action changes the scene, off on loaded/error
  const tabRef = useRef("today");
  tabRef.current = tab;
  const [reduceMotion, setReduceMotion] = useState(() => localStorage.getItem("jarvis_reduce_motion") === "1");
  useEffect(() => {
    document.documentElement.dataset.reduceMotion = reduceMotion ? "1" : "0";
    localStorage.setItem("jarvis_reduce_motion", reduceMotion ? "1" : "0");
  }, [reduceMotion]);

  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);
  const goTo = useCallback((screen) => { setTab(screen); setMenuOpen(false); setSpotlight(null); if (screen !== "today") setChatOpen(false); }, []);
  const endSweep = useCallback(() => setSweep(false), []);
  // Spotlight finished loading REAL content: stop the transition and give one honest cue.
  const spotStatus = useCallback((s) => { setSweep(false); sceneCue(s === "ready" ? "ok" : "error"); }, []);
  const toScene = useCallback(() => { setSpotlight(null); setFocus(null); setSweep(false); setTab("today"); setMenuOpen(false); }, []);
  const runActions = useCallback((actions) => {
    if (!actions?.length) return;
    // A write was staged because untrusted email/news was read this turn: show the on-screen confirm
    // bound to the exact target. Approving calls the server, which executes only the stored action.
    const conf = [...actions].reverse().find((a) => a.type === "confirm_action");
    if (conf) setPendingConfirm(conf);
    // real tool-driven UI widgets (mail/calendar/document/map/research cards) — same path for chat & Live
    actions.filter((a) => a.type === "widget").forEach((a) => widgetEmit("add", a.widget || {}));
    actions.filter((a) => a.type === "widget_control").forEach((a) => widgetEmit(a.op || "clear", a.widget || {}));
    // Pan/zoom the open map (Live voice or a tool). No open map → honest feedback, never a silent no-op.
    actions.filter((a) => a.type === "map_control").forEach((a) => {
      if (!mapControl(a)) toast.info(tx("Harita açık değil; önce bir konum açın."));
    });
    const openRec = [...actions].reverse().find((a) => a.type === "open_record");
    const last = actions[actions.length - 1];
    if (openRec) {
      setFocus({ kind: openRec.kind, id: openRec.id });
      if (SPOT_KINDS.has(openRec.kind)) { setSweep(true); setSpotlight(openRec); setTab(openRec.screen); setMenuOpen(false); setChatOpen(false); }
      else { if (openRec.screen !== tabRef.current) setSweep(true); goTo(openRec.screen); }
    } else if (last?.screen) {
      setFocus(null);
      if (last.screen !== tabRef.current && last.screen !== "today") setSweep(true);
      goTo(last.screen);
    }
  }, [goTo]);

  const doConfirmAction = useCallback(async () => {
    if (!pendingConfirm) return;
    try {
      const r = await api.post(`/actions/${pendingConfirm.action_id}/confirm`,
        { idempotency_key: `cf_${pendingConfirm.action_id}` });
      toast.success(r.data?.duplicate_suppressed ? tx("Zaten uygulandı") : `${tx("Onaylandı")}: ${pendingConfirm.summary}`);
      // confirmed tool may surface a real widget (e.g. music player command) — same action path as chat/Live
      (r.data?.actions || []).filter((a) => a.type === "widget").forEach((a) => widgetEmit("add", a.widget || {}));
      bump();
    } catch (e) {
      // expired (410) / already used or unknown (409): nothing was executed — say so and drop the stale card
      toast.error(tx(e?.response?.data?.detail || "Onay uygulanamadı; işlemi yeniden isteyin."));
    }
    setPendingConfirm(null);
  }, [pendingConfirm, bump, tx]);

  const closeTalk = useCallback(() => {
    if (document.querySelector('[data-testid="voice-note-stop-btn"]')) {
      window.dispatchEvent(new CustomEvent("jarvis-toast", { detail: tx("Kayıt sürüyor — önce 'Kaydı bitir' deyin.") }));
      return false;
    }
    window.dispatchEvent(new Event("jarvis-stop-speech"));
    setTalkOpen(false);
    return true;
  }, [tx]);
  const checkAi = useCallback(async () => {
    try { const r = await api.get("/ai/status"); setAiStatus(r.data); setOrbState(r.data.connected ? "idle" : "disconnected"); }
    catch (e) {}
  }, []);
  const SOURCE_NAME = { emergent: "Platform", openai: "OpenAI", anthropic: "Claude", local: "AI PC" };
  const aiLabel = aiStatus?.connected ? `${t("aiConnected")} · ${SOURCE_NAME[aiStatus.provider] || aiStatus.provider}`
    : aiStatus?.configured && !aiStatus?.last_test ? `${tx("AI yapılandırıldı · test edilmedi")} · ${SOURCE_NAME[aiStatus.provider] || ""}`
    : aiStatus?.reason === "insufficient_quota" ? tx("AI bakiye yetersiz")
    : aiStatus?.reason === "no_key" ? tx("AI anahtar yok")
    : aiStatus?.reason === "invalid_key" ? tx("AI anahtar geçersiz")
    : aiStatus ? tx("AI erişim doğrulanmadı") : t("aiNotConnected");

  useEffect(() => {
    if (!user) { navigate("/"); return; }
    api.post("/bootstrap").then(() => bump()).catch(() => {});
    api.get("/settings").then((r) => {
      const vm = r.data?.voice_mode || (r.data?.voice_live_mode ? "live" : "standard");
      setVoiceMode(["live", "standard", "off"].includes(vm) ? vm : "standard");
      setWakeEnabled(!!r.data?.wake_word_enabled);
      if (r.data?.language && r.data.language !== lang) setLang(r.data.language);
    }).catch(() => setVoiceMode("standard"));
    checkAi();
  }, [user, navigate, checkAi, bump]);

  // Panels (e.g. project memory sources) open real records through the same action path as chat/Live.
  useEffect(() => {
    const h = (e) => runActions(Array.isArray(e.detail) ? e.detail : []);
    window.addEventListener("jarvis-actions", h);
    return () => window.removeEventListener("jarvis-actions", h);
  }, [runActions]);

  // Settings toggles the wake-word preference live (no reload needed).
  useEffect(() => {
    const h = (e) => setWakeEnabled(!!(e.detail && e.detail.enabled));
    window.addEventListener("jarvis-wake-set", h);
    return () => window.removeEventListener("jarvis-wake-set", h);
  }, []);

  // Selecting a voice mode only saves the preference — it never opens a (paid) session. The running engine is
  // stopped FIRST (mic + session released), then the other engine is mounted.
  const changeVoiceMode = useCallback(async (mode) => {
    try { voiceRef.current?.stop?.(); } catch (e) { /* engine already stopped */ }
    await api.put("/ai/voice-mode", { mode });
    setVoiceConn({ status: "off" });
    setVoiceMode(mode);
    setOrbState("idle");
  }, []);
  const changeLiveMode = useCallback((next) => changeVoiceMode(next ? "live" : "standard"), [changeVoiceMode]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const onScene = tabRef.current === "today";
      if (menuOpen || chatOpen || (talkOpen && onScene)) { setMenuOpen(false); setChatOpen(false); if (onScene) closeTalk(); return; }
      if (!onScene) toScene(); // spotlight or panel → back to the plain scene
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeTalk, toScene, menuOpen, chatOpen, talkOpen]);

  // The centre orb IS the voice control for both modes (one mounted engine, no second mic).
  const toggleVoice = useCallback(() => {
    if (voiceMode === "off") { toast.info(tx("Ses kapalı — Ayarlar → Yapay zekâ ve ses'ten açabilirsiniz.")); return; }
    voiceRef.current?.toggle();
  }, [voiceMode, tx]);

  if (!user) return null;
  const isScene = tab === "today";
  const talkChat = isScene && talkOpen && !chatOpen;   // Yazışma fills the stage, orb sits in the corner
  const contentInScene = isScene && bigWidget && !talkChat;  // big widget/map open → orb morphs to the corner

  const renderPanel = () => {
    const key = `${tab}-${refreshKey}`;
    const fid = focus?.id;
    switch (tab) {
      case "tasks": return <TasksPanel onChanged={bump} focusId={fid} key={key} />;
      case "projects": return <ProjectsPanel onChanged={bump} focusId={fid} key={key} />;
      case "clients": return <ClientsPanel onChanged={bump} focusId={fid} key={key} />;
      case "proposals": return <ProposalsPanel onChanged={bump} focusId={fid} key={key} />;
      case "finance": return <FinancePanel key={key} />;
      case "emails": return <EmailsPanel onChanged={bump} key={key} />;
      case "files": return <FilesPanel onChanged={bump} focusId={fid} key={key} />;
      case "calendar": return <CalendarPanel key={key} />;
      case "notifications": return <NotificationsPanel key={key} />;
      case "memory": return <MemoryPanel key={key} />;
      case "usage": return <UsagePanel key={key} />;
      case "site": return <FilesPanel onChanged={bump} siteMode focusId={fid} key={key} />;
      case "settings": return <SettingsPanel aiStatus={aiStatus} onRecheck={checkAi} reduceMotion={reduceMotion} setReduceMotion={setReduceMotion}
        liveMode={liveMode} onLiveModeChange={changeLiveMode} onVoiceModeChange={changeVoiceMode}
        liveConn={liveMode ? voiceConn : { status: "off" }} key={key} />;
      default: return null;
    }
  };

  const voiceActive = ["starting", "live", "closing"].includes(voiceConn.status);

  const chat = (compact, roomy = false) => (
    <ChatVoice setOrbState={setOrbState}
      aiProvider={aiStatus?.provider} onDataChanged={bump} onActions={runActions} compact={compact} roomy={roomy}
      onExpand={() => setChatOpen(true)} defaultModel={user?.preferences?.default_model || "auto"} />
  );

  return (
    <MotionConfig reducedMotion={reduceMotion ? "always" : "user"}>
    <LayoutGroup>
    <div className="h-screen hud-bg flex flex-col overflow-hidden">
      <SceneBackdrop state={orbState} dimmed={!isScene} />
      {isScene && (
        <div className="scene-frame" data-active={(voiceActive || bigWidget || talkChat) ? "1" : "0"}
          aria-hidden="true" data-testid="scene-frame">
          <i className="tl" /><i className="tr" /><i className="bl" /><i className="br" />
        </div>
      )}
      {/* Minimal header */}
      <header className="relative z-20 flex items-center justify-between px-3 lg:px-5 py-2.5">
        <div className="flex items-center gap-2">
          {!isScene && (
            <button onClick={toScene} data-testid="back-to-scene-btn"
              className="flex items-center gap-1.5 px-3 h-10 rounded-full border border-white/10 text-sm text-white/70 hover:text-cyan-200 hover:border-cyan-400/40">
              <ArrowLeft size={15} /> {tx("Asistan")}
            </button>
          )}
        </div>
        <div className="w-10" aria-hidden="true" data-testid="header-right-empty" />
      </header>

      {/* Stage / content */}
      <main className={`flex-1 relative z-10 ${talkChat ? "overflow-hidden" : "overflow-y-auto scrollbar-thin"}`}>
        {isScene && <WidgetDeck onBigChange={setBigWidget} onMapScene={setMapScene} />}
        {isScene && bigWidget && !talkChat && (
          <div className="absolute bottom-24 right-4 z-30 pointer-events-none" data-testid="corner-orb">
            <JarvisOrb state={orbState} size={64} compact />
          </div>
        )}
        {/* Map open: the orb shrinks to a corner (tappable voice control) so it never covers the map,
            search, zoom or close controls. It returns to the centre when the map closes. */}
        {isScene && mapScene && !talkChat && (
          <div className="absolute top-16 right-3 z-[650]" data-testid="map-corner-orb">
            <JarvisOrb state={orbState} size={66} compact onActivate={toggleVoice} active={voiceActive} />
          </div>
        )}
        {isScene ? (
          talkChat ? (
            /* Yazışma open: the chat takes almost the whole stage, the orb has morphed to the corner */
            <motion.div key="talk" role="dialog" aria-label={tx("Jarvis ile konuş")} data-testid="talk-panel"
              className="h-full flex flex-col px-2 sm:px-4 pt-1 pb-[max(0.25rem,env(safe-area-inset-bottom))] relative"
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.26, ease: EASE }}>
              <button onClick={closeTalk} data-testid="talk-close-btn" aria-label={tx("Yazışmayı kapat")}
                className="absolute top-2 right-3 sm:right-5 z-20 h-9 w-9 rounded-full bg-black/55 border border-white/10 text-white/70 hover:text-white flex items-center justify-center"><X size={16} /></button>
              <div className="flex-1 min-h-0">{chat(false, true)}</div>
            </motion.div>
          ) : (
            <div className={`min-h-full flex flex-col items-center justify-center px-4 pt-3 pb-4 gap-4 relative ${mapScene ? "map-active" : ""}`}
              data-testid="assistant-scene">
              <TodayPanel orbState={orbState} onActivate={toggleVoice} sessionActive={voiceActive}
                showOrb={!contentInScene && !mapScene} orbSize={voiceActive ? 420 : 190} />
            </div>
          )
        ) : spotlight ? (
          <RecordSpotlight key={`${spotlight.kind}-${spotlight.id}`} kind={spotlight.kind} id={spotlight.id} label={spotlight.label}
            onClose={toScene} onShowList={() => setSpotlight(null)} onStatus={spotStatus} />
        ) : (
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.24, ease: EASE }}
            onAnimationComplete={endSweep} className="p-4 lg:p-8 max-w-6xl mx-auto pb-28" data-testid={`panel-wrap-${tab}`}>
            {renderPanel()}
          </motion.div>
        )}
      </main>

      {/* HUD transition — only while a real action is loading its content; never on a timer */}
      <AnimatePresence>
        {sweep && !reduceMotion && (
          <motion.div key="sweep" className="hud-sweep" data-testid="hud-sweep" aria-hidden="true"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }} />
        )}
      </AnimatePresence>

      {/* Voice session: bottom row of the layout. Exactly ONE engine is mounted — the chosen voice mode
          (Live = own OpenAI WebRTC, off = Emergent chain) — so two mics/players can never run together. */}
      <div className={`shrink-0 relative z-30 flex flex-col items-center gap-2 px-3 ${talkChat ? "pt-0 pb-1" : "pt-1 pb-3"}`} data-testid="voice-bar">
        {/* grouped control dock — menu, Yazışma, voice. Settings live inside the menu. */}
        <div className="dock" role="toolbar" aria-label={t("menu")} data-testid="control-dock">
          <button className="dock-btn" data-testid="menu-btn" aria-label={t("menu")} aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}><Menu size={18} /><span className="tip">{t("menu")}</span></button>
          <button className="dock-btn" data-testid="talk-open-btn" aria-label={t("talk")} aria-pressed={talkOpen}
            data-on={talkOpen ? "1" : "0"}
            onClick={() => { if (talkChat) { closeTalk(); } else { setTab("today"); setChatOpen(false); setTalkOpen(true); } }}>
            <MessageSquare size={18} /><span className="tip">{t("talk")}</span></button>
          <button className="dock-btn" data-testid="dock-voice-btn" data-on={voiceActive ? "1" : "0"}
            aria-pressed={voiceActive} aria-label={voiceActive ? t("endConv") : t("startConv")}
            onClick={toggleVoice}><Mic size={18} /><span className="tip">{voiceActive ? t("endConv") : t("startConv")}</span></button>
        </div>
        {voiceMode === null ? <div className="h-6" data-testid="voice-bar-loading" />
          : voiceMode === "off" ? <span className="sr-only" data-testid="voice-off">{tx("Ses kapalı")}</span>
          : liveMode ? (
            <LiveSession ref={voiceRef} setOrbState={setOrbState} lang={lang} onActions={runActions} compact={!isScene || talkChat}
              onStatus={setVoiceConn} onDisableLive={() => changeLiveMode(false)} />
          ) : (
            <VoiceSession ref={voiceRef} setOrbState={setOrbState} lang={lang} onActions={runActions}
              compact={!isScene || talkChat} onStatus={setVoiceConn} />
          )}
      </div>

      {/* Free wake-word listener (SpeechRecognition only — opens NO paid session while waiting). On "Jarvis" it
          starts the real session via the shared voiceRef; it pauses while a session is active. */}
      <WakeWord enabled={wakeEnabled && voiceMode !== "off" && voiceMode !== null} sessionActive={voiceActive}
        onWake={() => voiceRef.current?.start()} />

      {/* Corner orb — the SAME orb (layoutId morph). On an open panel it returns to the scene; with Yazışma
          open it stays the voice control (start/stop) and gets its own reserved spot, clear of the input row. */}
      {(!isScene || talkChat) && (
        talkChat ? (
          <div className="fixed z-30 bottom-16 right-3 sm:bottom-20 sm:right-6" data-testid="corner-orb-slot">
            <JarvisOrb state={orbState} size={76} compact onActivate={toggleVoice} active={voiceActive} />
          </div>
        ) : (
          <button data-testid="mini-orb" onClick={toScene} title={tx("Asistana dön")}
            className="fixed z-30 rounded-full bottom-5 left-4 sm:left-auto sm:right-5">
            <JarvisOrb state={orbState} size={72} compact />
          </button>
        )
      )}

      {/* Drawer menu */}
      <AnimatePresence>
        {menuOpen && (
          <>
            <motion.div key="scrim" className="fixed inset-0 z-40 bg-black/50" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }} onClick={() => setMenuOpen(false)} data-testid="menu-scrim" />
            <motion.aside key="drawer" role="dialog" aria-label={t("menu")} data-testid="menu-drawer"
              className="fixed z-50 top-0 bottom-0 left-0 w-[260px] bg-[#0A0F1D]/95 backdrop-blur-xl border-r border-cyan-500/15 p-3 flex flex-col"
              initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }} transition={{ duration: 0.28, ease: EASE }}>
              <div className="flex items-center justify-between px-2 py-2 mb-2">
                <span className="label-eyebrow">{t("menu")}</span>
                <button onClick={() => setMenuOpen(false)} data-testid="menu-close-btn" className="text-white/60 hover:text-white p-1"><X size={18} /></button>
              </div>
              <div className="flex-1 overflow-y-auto scrollbar-thin">
              {favs.length > 0 && (
                <div className="mb-2" data-testid="nav-group-favorites">
                  <div className="label-eyebrow px-3 py-1.5 text-[0.62rem] text-amber-300/60">{tx("Favoriler")}</div>
                  {favs.filter((k) => ICON_BY_KEY[k]).map((key) => {
                    const Icon = ICON_BY_KEY[key];
                    return (
                      <button key={`fav-${key}`} onClick={() => goTo(key)} data-testid={`nav-fav-${key}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm mb-0.5 w-full ${tab === key ? "bg-cyan-500/15 text-cyan-200 border border-cyan-400/30 nav-active" : "text-white/65 hover:text-cyan-300 hover:bg-white/5"}`}>
                        <Icon size={17} /> {t(`nav_${key}`)}
                      </button>
                    );
                  })}
                </div>
              )}
              {NAV_GROUPS.map(([group, items]) => (
                <div key={group} className="mb-2">
                  <div className="label-eyebrow px-3 py-1.5 text-[0.62rem] text-white/35">{tx(group)}</div>
                  {items.map(([key, Icon]) => (
                    <div key={key} className={`group flex items-center rounded-lg mb-0.5 ${tab === key ? "bg-cyan-500/15 border border-cyan-400/30 nav-active" : "hover:bg-white/5"}`}>
                      <button onClick={() => goTo(key)} data-testid={`nav-${key}`} autoFocus={key === tab}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm flex-1 ${tab === key ? "text-cyan-200" : "text-white/65 group-hover:text-cyan-300"}`}>
                        <Icon size={17} /> {t(`nav_${key}`)}
                      </button>
                      <button onClick={() => toggleFav(key)} data-testid={`nav-fav-toggle-${key}`} title={tx("Favorilere ekle/çıkar")}
                        className={`px-2 py-2 ${favs.includes(key) ? "text-amber-300" : "text-white/25 hover:text-amber-300 opacity-0 group-hover:opacity-100"}`}>
                        <Star size={14} fill={favs.includes(key) ? "currentColor" : "none"} />
                      </button>
                    </div>
                  ))}
                </div>
              ))}
              </div>
              <div className="border-t border-white/10 pt-3 mt-2 space-y-2" data-testid="drawer-footer">
                <button onClick={() => goTo("settings")} data-testid="ai-status-pill"
                  className={`w-full flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-[0.68rem] font-mono ${aiStatus?.connected ? "bg-emerald-500/10 border-emerald-400/30 text-emerald-300" : "bg-red-500/10 border-red-400/30 text-red-300"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${aiStatus?.connected ? "bg-emerald-400" : "bg-red-400"}`} />{aiLabel}
                </button>
                <div className="flex items-center justify-between px-1">
                  <div className="flex rounded-full border border-white/10 overflow-hidden">
                    {["tr", "en"].map((l) => (
                      <button key={l} onClick={() => { setLang(l); api.put("/settings", { language: l }).catch(() => {}); }} data-testid={`header-lang-${l}`}
                        className={`px-2.5 py-1 text-xs font-mono ${lang === l ? "bg-cyan-500/20 text-cyan-200" : "text-white/50"}`}>{l.toUpperCase()}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 min-w-0">
                    {user.picture ? <img src={user.picture} alt="" className="h-7 w-7 rounded-full border border-white/15" />
                      : <div className="h-7 w-7 rounded-full bg-cyan-500/15 flex items-center justify-center text-cyan-200 text-xs">{user.name?.[0]}</div>}
                    <span className="text-xs text-white/60 truncate max-w-[110px]" data-testid="drawer-user-email">{user.email}</span>
                    <button onClick={logout} data-testid="logout-btn" title={t("logout")} className="text-white/40 hover:text-red-400 p-1"><LogOut size={16} /></button>
                  </div>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Chat overlay (opt-in) */}
      <AnimatePresence>
        {chatOpen && (
          <motion.section key="chat" role="dialog" aria-label={tx("Sohbet")} data-testid="chat-overlay"
            className="fixed z-40 inset-x-0 bottom-0 top-14 sm:inset-auto sm:top-16 sm:right-4 sm:bottom-4 sm:w-[420px] p-2 sm:p-0"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 16 }} transition={{ duration: 0.26, ease: EASE }}>
            <div className="h-full relative">
              <button onClick={() => { window.dispatchEvent(new Event("jarvis-stop-speech")); setChatOpen(false); }} data-testid="chat-close-btn" aria-label={t("close")}
                className="absolute -top-1 right-2 z-10 h-8 w-8 rounded-full bg-black/40 border border-white/10 text-white/70 hover:text-white flex items-center justify-center"><X size={15} /></button>
              {chat(false)}
            </div>
          </motion.section>
        )}
      </AnimatePresence>

      {pendingConfirm && (
        <ReviewConfirm testid="action-confirm-modal"
          title={tx("Güvenlik onayı")}
          rows={[[tx("İşlem"), pendingConfirm.summary], [tx("Tür"), pendingConfirm.tool]]}
          warning={tx("Bu yanıtta e-posta/haber gibi güvenilmez dış içerik okundu. İşlem yalnızca bu onayla ve yukarıda gösterilen hedefte yapılır.")}
          confirmLabel={tx("Onayla ve uygula")}
          onConfirm={doConfirmAction}
          onCancel={() => setPendingConfirm(null)} />
      )}
    </div>
    </LayoutGroup>
    </MotionConfig>
  );
}
